import { useEffect, useState } from 'react';
import { bindActionCreators } from '@reduxjs/toolkit';
import { connect } from 'react-redux';
import I18njs from 'i18n-js';
import moment from 'moment';
import { useHistory } from 'react-router-dom';

import Button from 'carbon-react/lib/components/button';
import DateInput from 'carbon-react/lib/components/date';
import Modal from 'carbon-react/lib/components/dialog';
import Loader from 'carbon-react/lib/components/loader';
import Textarea from 'carbon-react/lib/components/textarea';

import Translate from '../../components/Translate/Translate';

import { TimesheetStartDayValidator } from '../../helpers/validations';
import { useLocationQuery } from '../../helpers/hooks';

import * as TimesheetActions from '../../redux/Timesheet/actions';

const CreateTimesheet = ({ company, createTimesheet, duplicate, duplicateTimesheet, onCancel, timesheet, timesheets }) => {
  const { timesheetDuration, weekStart } = company.data;
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState(null);

  const history = useHistory();
  const query = useLocationQuery();
  const queryStartDate = query.get('start_date');
  const queryEndDate = query.get('end_date');

  useEffect(() => {
    if (queryStartDate && queryEndDate) {
      const start = moment(decodeURIComponent(queryStartDate)).format('YYYY-MM-DD');
      const end = moment(decodeURIComponent(queryEndDate)).format('YYYY-MM-DD');
      setStartDate(start);
      setEndDate(end);
    } else {
      let initialStartDate;
      let initialEndDate;
      if (!duplicate) {
        if (timesheetDuration === 15) {
          if (moment().date() <= 15) {
            initialStartDate = moment().startOf('month').format('YYYY-MM-DD');
            initialEndDate = moment().startOf('month').add(14, 'days').format('YYYY-MM-DD');
          } else {
            initialStartDate = moment().startOf('month').add(15, 'days').format('YYYY-MM-DD');
            initialEndDate = moment().endOf('month').format('YYYY-MM-DD');
          }
        } else {
          let _start = moment(weekStart - 1, 'd');
          if (_start.isAfter(moment())) _start = _start.subtract(1, 'week');
          initialStartDate = _start.format('YYYY-MM-DD');
          initialEndDate = _start.add(timesheetDuration - 1, 'days').format('YYYY-MM-DD');
        }
      } else {
        initialStartDate = timesheet.startDate.substring(0, 10);
        initialEndDate = timesheet.endDate.substring(0, 10);
        setDescription(`${timesheet.description} copy`);
      }
      setStartDate(initialStartDate);
      setEndDate(initialEndDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicate, timesheetDuration, weekStart, queryStartDate, queryEndDate]);

  const handleStartDateChange = (e) => {
    const rawStartDate = e.target.value.rawValue;
    setStartDate(rawStartDate);
    const validation = new TimesheetStartDayValidator();
    if (!validation.validate(rawStartDate)) {
      setError(validation.message());
    } else {
      setError();
    }
    const beginMoment = moment(rawStartDate, 'YYYY-MM-DD');
    let endMoment;
    if (timesheetDuration === 15) {
      endMoment = beginMoment.date() === 1 ? beginMoment.clone().startOf('month').add(14, 'days') : beginMoment.clone().endOf('month');
    } else {
      endMoment = beginMoment.add(timesheetDuration - 1, 'days');
    }

    const rawEndDate = endMoment.format('YYYY-MM-DD');
    setEndDate(rawEndDate);
  };

  const handleDescriptionChange = (e) => {
    e.persist();
    setDescription(e.target.value);
  };

  const handleSubmit = () => {
    if (startDate === '' || endDate === '') return;
    if (!duplicate && !new TimesheetStartDayValidator().validate(startDate)) return;
    const data = {
      startDate,
      endDate,
    };
    if (description !== '') {
      data.description = description;
    }
    if (duplicate) {
      duplicateTimesheet({
        data,
        history,
        id: timesheet.id,
      });
    } else {
      createTimesheet({
        data,
        history,
      });
    }
    onCancel();
  };

  const onCloseModal = () => {
    history.push('/timesheets');
    onCancel();
  };

  return (
    <Modal
      disableAutoFocus={true}
      disableEscKey={false}
      enableBackgroundUI={false}
      onCancel={onCloseModal}
      open={true}
      showCloseIcon={true}
      title={I18njs.t(duplicate ? 'timesheets.DuplicateTimesheet' : 'timesheets.CreateTimesheet')}
      data-cy="create-timesheet-modal"
    >
      <div className="create-timesheet" data-cy="create-timesheet-master-div">
        <div className="inputs">
          <div className="begin-date" data-cy="create-timesheet-begin-date-label">
            <DateInput
              allowEmptyValue
              error={error}
              formattedValue={moment(startDate, 'YYYY-MM-DD').format('MM/DD/YYYY')}
              label={I18njs.t('timesheets.BeginDate')}
              name="start-date-input"
              onChange={(e) => handleStartDateChange(e)}
              value={startDate}
            />
          </div>
          <div className="end-date" data-cy="create-timesheet-end-date-label">
            <DateInput
              allowEmptyValue
              formattedValue={moment(endDate, 'YYYY-MM-DD').format('MM/DD/YYYY')}
              disabled
              label={I18njs.t('timesheets.EndDate')}
              name="end-date-input"
              onChange={(e) => setEndDate(e.target.value.rawValue)}
            />
          </div>
          <div className="description fs-exclude" data-cy="create-timesheet-description-label">
            <Textarea
              autoFocus
              label={I18njs.t('timesheets.Description')}
              name="description"
              onChange={(e) => handleDescriptionChange(e)}
              value={description}
            />
          </div>
        </div>
        <div className="footer-buttons">
          <Button buttonType="tertiary" onClick={onCloseModal} data-cy="create-timesheet-cancel-button">
            <Translate scope="timesheets.Cancel" />
          </Button>
          <Button as="primary" onClick={handleSubmit} ml={1} data-cy="create-timesheet-create-timesheet-button">
            {timesheets.saving ? (
              <Loader isInsideButton />
            ) : (
              <Translate scope={duplicate ? 'timesheets.DuplicateTimesheet' : 'timesheets.CreateTimesheet'} />
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  company: state.company,
  timesheets: state.timesheets,
});

const mapDispatchToProps = (dispatch) => {
  const _timesheet = bindActionCreators(TimesheetActions, dispatch);
  return {
    createTimesheet: _timesheet.createTimesheetRequest,
    duplicateTimesheet: _timesheet.duplicateTimesheetRequest,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateTimesheet);
